<script setup lang="ts">
/**
 * 顶栏账号入口（设计文档 §5.3）。
 *
 *   · 加载中（含暂时性故障的自动重试期间）：与「登录」按钮同尺寸的占位骨架，避免顶栏跳动；
 *   · 未登录（/api/v1/me 返回 401）：「登录」按钮，整页跳到 /api/v1/auth/login?returnTo=/me；
 *   · 状态未知（重试后仍失败）：同样给「登录」按钮，登录后回到当前页 —— 顶栏任何时候都不能没有入口；
 *   · 已登录：头像 + 昵称，下拉「我的应用」「切换账号」「退出登录」；
 *   · 开关关闭（404 / 功能未开启业务码）：什么都不渲染，顶栏与原来完全一致（入口本身不可用）。
 *
 * 放在 .official-nav 之外（页头右侧 .official-header-actions 里，「下载 App」之前），
 * 不碰 .official-nav 那套 flex gap 回退规则（#38）；本组件自身不用 flex gap。
 */
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue'
import { accountState, buildLoginUrl, currentReturnPath, describeLoginError, loadAccount, loginError, logout, MyAppsPath, switchAccount } from '../account'
import { Icon } from './Icon'

const props = defineProps<{
  /** 当前就在「我的应用」页时，下拉里的入口标为当前页。 */
  current?: 'me'
}>()

const loginUrl = buildLoginUrl(MyAppsPath)
const menuOpen = ref(false)
/** 正在进行的会话操作（退出 / 切换账号），两者互斥。 */
const busy = ref<'' | 'logout' | 'switch'>('')
const actionError = ref('')
const avatarFailed = ref(false)
const root = useTemplateRef<HTMLElement>('root')
const trigger = useTemplateRef<HTMLButtonElement>('trigger')
const menu = useTemplateRef<HTMLElement>('menu')
const menuId = 'official-account-menu'

const user = computed(() => (accountState.value.kind === 'signed-in' ? accountState.value.user : null))
const displayName = computed(() => (user.value && user.value.name) || '蜜罐用户')
/** 头像加载失败或没有头像时，用昵称首字做占位。 */
const initial = computed(() => displayName.value.charAt(0).toUpperCase())

/* ── 下拉菜单：按钮语义 + 键盘操作（Esc 关闭并把焦点还给按钮，方向键 / Tab 循环切换项）── */

function menuItems(): HTMLElement[] {
  return menu.value ? Array.prototype.slice.call(menu.value.querySelectorAll('[role="menuitem"]')) : []
}

async function openMenu(focusIndex = -1): Promise<void> {
  menuOpen.value = true
  actionError.value = ''
  await nextTick()
  const items = menuItems()
  if (focusIndex >= 0 && items.length) items[Math.min(focusIndex, items.length - 1)].focus()
  else if (focusIndex < -1 && items.length) items[items.length - 1].focus()
}

function closeMenu(returnFocus = false): void {
  if (!menuOpen.value) return
  menuOpen.value = false
  if (returnFocus) trigger.value?.focus()
}

function toggleMenu(): void {
  if (menuOpen.value) closeMenu()
  else void openMenu()
}

function onTriggerKeydown(event: KeyboardEvent): void {
  if ((event.key === 'Escape' || event.key === 'Esc') && menuOpen.value) {
    event.preventDefault()
    closeMenu(true)
  } else if (event.key === 'ArrowDown' || event.key === 'Down') {
    event.preventDefault()
    void openMenu(0)
  } else if (event.key === 'ArrowUp' || event.key === 'Up') {
    event.preventDefault()
    void openMenu(-2)
  }
}

function onMenuKeydown(event: KeyboardEvent): void {
  const items = menuItems()
  const index = items.indexOf(document.activeElement as HTMLElement)
  switch (event.key) {
    case 'Escape':
    case 'Esc':
      event.preventDefault()
      closeMenu(true)
      break
    case 'ArrowDown':
    case 'Down':
      event.preventDefault()
      if (items.length) items[(index + 1) % items.length].focus()
      break
    case 'ArrowUp':
    case 'Up':
      event.preventDefault()
      if (items.length) items[(index - 1 + items.length) % items.length].focus()
      break
    case 'Home':
      event.preventDefault()
      if (items.length) items[0].focus()
      break
    case 'End':
      event.preventDefault()
      if (items.length) items[items.length - 1].focus()
      break
    case 'Tab':
      // 菜单打开期间 Tab / Shift+Tab 在菜单项之间循环；Esc 或点外部才关闭
      event.preventDefault()
      if (items.length) {
        const step = event.shiftKey ? -1 : 1
        items[(index + step + items.length) % items.length].focus()
      }
      break
  }
}

/** 点到菜单外面就收起（鼠标与触屏都覆盖）。 */
function onDocumentPointer(event: Event): void {
  if (!menuOpen.value || !root.value) return
  if (!root.value.contains(event.target as Node)) closeMenu()
}

async function handleLogout(): Promise<void> {
  if (busy.value) return
  busy.value = 'logout'
  actionError.value = ''
  const result = await logout()
  if (result === 'redirected') return // 页面即将跳到 SSO 登出地址，保持「正在退出」
  busy.value = ''
  if (result === 'failed') {
    actionError.value = '退出失败，请检查网络后重试。'
    return
  }
  // 会话早已失效：本地已切成未登录，菜单随之消失
  menuOpen.value = false
}

/** 切换账号：退出当前会话后直接进登录流程（强制重新输入凭据），登录后回到当前页。 */
async function handleSwitch(): Promise<void> {
  if (busy.value) return
  busy.value = 'switch'
  actionError.value = ''
  const result = await switchAccount(currentReturnPath())
  if (result === 'redirected') return // 页面即将跳到登录入口，保持「正在切换」
  busy.value = ''
  actionError.value = '切换失败，请检查网络后重试。'
}

/**
 * 状态未知（请求失败）时的「登录」：点击时再取当前路径，登录后回到这里。
 * href 仍保留一个可用的默认地址（新标签打开 / 脚本异常时照样能进登录流程）。
 */
const fallbackLoginUrl = buildLoginUrl(currentReturnPath())
function handleFallbackLogin(event: MouseEvent): void {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
  event.preventDefault()
  window.location.assign(buildLoginUrl(currentReturnPath()))
}

/* ── 登录回调失败提示（/?login_error=<code>）────────────────────────────────── */

const noticeVisible = ref(!!loginError.value)
const noticeMessage = computed(() => describeLoginError(loginError.value))

function dismissNotice(): void {
  noticeVisible.value = false
}

onMounted(() => {
  void loadAccount()
  document.addEventListener('mousedown', onDocumentPointer)
  document.addEventListener('touchstart', onDocumentPointer)
})

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onDocumentPointer)
  document.removeEventListener('touchstart', onDocumentPointer)
})
</script>

<template>
  <div v-if="accountState.kind === 'loading'" class="official-account" data-account-state="loading" role="status">
    <span id="account-skeleton" class="official-account-skeleton" aria-hidden="true"></span>
    <span class="official-me-visually-hidden">正在获取登录状态</span>
  </div>
  <div v-else-if="accountState.kind === 'anonymous'" class="official-account" data-account-state="anonymous">
    <a id="account-login" class="official-account-login" :href="loginUrl"><Icon icon="lucide:user-round" aria-hidden="true" /><span>登录</span></a>
  </div>
  <div v-else-if="accountState.kind === 'error'" class="official-account" data-account-state="error">
    <a id="account-login" class="official-account-login" :href="fallbackLoginUrl" @click="handleFallbackLogin"><Icon icon="lucide:user-round" aria-hidden="true" /><span>登录</span></a>
  </div>
  <div v-else-if="user" ref="root" class="official-account" data-account-state="signed-in">
    <button
      id="account-trigger"
      ref="trigger"
      class="official-account-trigger"
      type="button"
      aria-haspopup="menu"
      :aria-expanded="menuOpen ? 'true' : 'false'"
      :aria-controls="menuId"
      :aria-label="`账号：${displayName}`"
      @click="toggleMenu"
      @keydown="onTriggerKeydown"
    >
      <span class="official-account-avatar" aria-hidden="true">
        <img v-if="user.avatar && !avatarFailed" :src="user.avatar" alt="" width="32" height="32" loading="lazy" decoding="async" referrerpolicy="no-referrer" @error="avatarFailed = true" />
        <span v-else>{{ initial }}</span>
      </span>
      <span class="official-account-name">{{ displayName }}</span>
      <Icon class="official-account-caret" icon="lucide:chevron-down" aria-hidden="true" />
    </button>
    <div
      v-show="menuOpen"
      :id="menuId"
      ref="menu"
      class="official-account-menu"
      role="menu"
      aria-labelledby="account-trigger"
      @keydown="onMenuKeydown"
    >
      <div class="official-account-menu-head" role="presentation">
        <span class="official-account-avatar official-account-avatar-lg" aria-hidden="true">
          <img v-if="user.avatar && !avatarFailed" :src="user.avatar" alt="" width="40" height="40" loading="lazy" decoding="async" referrerpolicy="no-referrer" @error="avatarFailed = true" />
          <span v-else>{{ initial }}</span>
        </span>
        <span class="official-account-menu-who">
          <strong>{{ displayName }}</strong>
          <small v-if="user.email">{{ user.email }}</small>
          <span class="official-account-menu-tag">聚点账号</span>
        </span>
      </div>
      <div class="official-account-menu-sep" role="separator"></div>
      <a
        id="account-menu-apps"
        class="official-account-menu-item"
        role="menuitem"
        :href="MyAppsPath"
        :aria-current="props.current === 'me' ? 'page' : undefined"
        tabindex="-1"
      >
        <Icon icon="lucide:layout-grid" aria-hidden="true" />
        <span>我的应用</span>
      </a>
      <button
        id="account-menu-switch"
        class="official-account-menu-item"
        role="menuitem"
        type="button"
        tabindex="-1"
        :disabled="!!busy"
        :aria-busy="busy === 'switch' ? 'true' : 'false'"
        @click="handleSwitch"
      >
        <Icon icon="lucide:arrow-left-right" aria-hidden="true" />
        <span>{{ busy === 'switch' ? '正在切换…' : '切换账号' }}</span>
      </button>
      <div class="official-account-menu-sep" role="separator"></div>
      <button
        id="account-menu-logout"
        class="official-account-menu-item"
        role="menuitem"
        type="button"
        tabindex="-1"
        :disabled="!!busy"
        :aria-busy="busy === 'logout' ? 'true' : 'false'"
        @click="handleLogout"
      >
        <Icon icon="lucide:log-out" aria-hidden="true" />
        <span>{{ busy === 'logout' ? '正在退出…' : '退出登录' }}</span>
      </button>
      <p v-if="actionError" class="official-account-menu-error" role="alert">{{ actionError }}</p>
    </div>
  </div>

  <!-- 登录回调失败提示：浮在页头下方，不占文档流，不影响任何既有布局 -->
  <Teleport to="body">
    <div v-if="noticeVisible" id="login-error-notice" class="official-login-notice" role="alert" :data-login-error="loginError">
      <span class="official-login-notice-icon" aria-hidden="true"><Icon icon="lucide:circle-alert" /></span>
      <div class="official-login-notice-body">
        <strong>登录未完成</strong>
        <p>{{ noticeMessage }}</p>
        <div class="official-login-notice-actions">
          <a id="login-error-retry" :href="loginUrl">重新登录</a>
          <button type="button" @click="dismissNotice">知道了</button>
        </div>
      </div>
      <button class="official-login-notice-close" type="button" aria-label="关闭提示" @click="dismissNotice"><Icon icon="lucide:x" aria-hidden="true" /></button>
    </div>
  </Teleport>
</template>
